import React, { useState, useEffect, useCallback } from 'react';
import {
  Folder, FileText, Image, Video, Download, ArrowLeft, ExternalLink,
  PlayCircle, X, Megaphone, Package, BookOpen, Share2, FileImage, Star,
  LucideIcon
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import { TutorialAutoStart, TutorialHelpButton } from '../../components/Portal/Tutorial';
import './PortalResources.css';

interface ResourceFolder {
  id: number;
  name: string;
  description: string;
  slug: string;
  icon: string;
  color: string;
  resource_count: number;
  total_resource_count: number;
  subfolders?: ResourceFolder[];
  breadcrumbs?: { id: number; name: string; slug: string }[];
}

interface Resource {
  id: number;
  title: string;
  description: string;
  resource_type: string;
  file_url: string | null;
  file_name: string;
  file_size_formatted: string;
  file_extension: string;
  youtube_url: string | null;
  youtube_video_id: string | null;
  is_video: boolean;
  thumbnail_url: string | null;
  is_featured: boolean;
  download_count: number;
}

const FOLDER_ICONS: Record<string, LucideIcon> = {
  folder: Folder,
  megaphone: Megaphone,
  image: Image,
  video: Video,
  'book-open': BookOpen,
  package: Package,
  'share-2': Share2,
  'play-circle': PlayCircle,
};

const FOLDER_COLORS: Record<string, string> = {
  gray: '#6b7280',
  blue: '#3b82f6',
  green: '#22c55e',
  yellow: '#eab308',
  red: '#ef4444',
  purple: '#a855f7',
  orange: '#f97316',
};

const RESOURCE_ICONS: Record<string, LucideIcon> = {
  document: FileText,
  image: Image,
  logo: FileImage,
  flyer: Megaphone,
  social_post: Share2,
  video: Video,
  feeding_guide: BookOpen,
  product_sheet: Package,
  other: FileText,
};

const PortalResources: React.FC = () => {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug?: string }>();

  const [folders, setFolders] = useState<ResourceFolder[]>([]);
  const [featured, setFeatured] = useState<Resource[]>([]);
  const [currentFolder, setCurrentFolder] = useState<ResourceFolder | null>(null);
  const [subfolders, setSubfolders] = useState<ResourceFolder[]>([]);
  const [resources, setResources] = useState<Resource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Video modal
  const [selectedVideo, setSelectedVideo] = useState<Resource | null>(null);

  // Load root folders
  const loadRootFolders = useCallback(async () => {
    try {
      const res = await api.get('/portal/resources/');
      setFolders(res.data.folders);
      setFeatured(res.data.featured || []);
    } catch (err) {
      console.error('Error loading resources:', err);
      setError('Failed to load resources');
    }
  }, []);

  // Load folder contents
  const loadFolderContents = useCallback(async (folderSlug: string) => {
    try {
      const res = await api.get(`/portal/resources/folder/${folderSlug}/`);
      setCurrentFolder(res.data.folder);
      setSubfolders(res.data.subfolders);
      setResources(res.data.resources);
    } catch (err) {
      console.error('Error loading folder:', err);
      setError('Failed to load folder');
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      setError('');

      if (slug) {
        await loadFolderContents(slug);
      } else {
        await loadRootFolders();
        setCurrentFolder(null);
        setSubfolders([]);
        setResources([]);
      }

      setLoading(false);
    };
    init();
  }, [slug, loadRootFolders, loadFolderContents]);

  // Track download
  const trackDownload = async (resourceId: number) => {
    try {
      await api.post(`/resources/${resourceId}/track-download/`);
    } catch (err) {
      console.error('Error tracking download:', err);
    }
  };

  // Handle download
  const handleDownload = (resource: Resource) => {
    if (resource.file_url) {
      trackDownload(resource.id);
      window.open(resource.file_url, '_blank');
    }
  };

  // Get folder icon
  const getFolderIcon = (iconName: string) => {
    return FOLDER_ICONS[iconName] || Folder;
  };

  // Get folder color
  const getFolderColor = (colorName: string) => {
    return FOLDER_COLORS[colorName] || '#6b7280';
  };

  // Get resource icon
  const getResourceIcon = (type: string) => {
    return RESOURCE_ICONS[type] || FileText;
  };

  // Check if file is an image based on extension
  const isImageFile = (extension: string | null | undefined): boolean => {
    if (!extension) return false;
    const imageExtensions = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'bmp'];
    return imageExtensions.includes(extension.toLowerCase());
  };

  // Get thumbnail URL for a resource (use file_url for images if no thumbnail)
  const getThumbnailUrl = (resource: Resource): string | null => {
    if (resource.thumbnail_url) return resource.thumbnail_url;
    if (isImageFile(resource.file_extension) && resource.file_url) {
      return resource.file_url;
    }
    return null;
  };

  // Render folder card
  const renderFolderCard = (folder: ResourceFolder) => {
    const IconComponent = getFolderIcon(folder.icon);
    const color = getFolderColor(folder.color);

    return (
      <div
        key={folder.id}
        className="folder-card"
        onClick={() => navigate(`/portal/resources/${folder.slug}`)}
      >
        <div className="folder-icon" style={{ backgroundColor: `${color}20`, color }}>
          <IconComponent size={24} />
        </div>
        <h3>{folder.name}</h3>
        {folder.description && <p>{folder.description}</p>}
        <span className="item-count">
          {folder.total_resource_count} {folder.total_resource_count === 1 ? 'item' : 'items'}
        </span>
      </div>
    );
  };

  // Render resource card
  const renderResourceCard = (resource: Resource) => {
    const IconComponent = getResourceIcon(resource.resource_type);
    const thumbnailUrl = getThumbnailUrl(resource);

    return (
      <div key={resource.id} className="resource-card">
        <div
          className="resource-thumbnail"
          onClick={() => {
            if (resource.is_video) {
              setSelectedVideo(resource);
            } else {
              handleDownload(resource);
            }
          }}
        >
          {resource.is_video && thumbnailUrl ? (
            <>
              <img src={thumbnailUrl} alt={resource.title} />
              <div className="play-overlay">
                <PlayCircle size={40} />
              </div>
            </>
          ) : thumbnailUrl ? (
            <img src={thumbnailUrl} alt={resource.title} className="resource-image-preview" />
          ) : (
            <div className="resource-icon-placeholder">
              <IconComponent size={32} />
            </div>
          )}
          {isImageFile(resource.file_extension) && !resource.is_video && (
            <span className="file-type-badge">{resource.file_extension?.toUpperCase()}</span>
          )}
        </div>
        <div className="resource-info">
          <h4>{resource.title}</h4>
          {resource.description && (
            <p className="resource-description">{resource.description}</p>
          )}
          <div className="resource-meta">
            {resource.file_extension && (
              <span className="file-type">{resource.file_extension}</span>
            )}
            {resource.file_size_formatted && (
              <span className="file-size">{resource.file_size_formatted}</span>
            )}
            {resource.is_video && <span className="video-tag">Video</span>}
          </div>
        </div>
        <div className="resource-actions">
          {resource.is_video ? (
            <button
              className="btn btn-primary"
              onClick={() => setSelectedVideo(resource)}
            >
              <PlayCircle size={16} />
              Watch
            </button>
          ) : resource.file_url ? (
            <button
              className="btn btn-primary"
              onClick={() => handleDownload(resource)}
            >
              <Download size={16} />
              Download
            </button>
          ) : null}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="portal-resources">
        <div className="loading-state">Loading resources...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="portal-resources">
        <div className="error-state">{error}</div>
      </div>
    );
  }

  return (
    <div className="portal-resources">
      <TutorialAutoStart pageKey="resources" />
      {/* Header */}
      <div className="resources-header">
        {currentFolder ? (
          <>
            <button
              className="back-button"
              onClick={() => {
                const breadcrumbs = currentFolder.breadcrumbs || [];
                if (breadcrumbs.length > 1) {
                  navigate(`/portal/resources/${breadcrumbs[breadcrumbs.length - 2].slug}`);
                } else {
                  navigate('/portal/resources');
                }
              }}
            >
              <ArrowLeft size={20} />
              Back
            </button>
            <div className="breadcrumbs">
              <span
                className="breadcrumb-link"
                onClick={() => navigate('/portal/resources')}
              >
                Resources
              </span>
              {currentFolder.breadcrumbs?.map((crumb, idx) => (
                <React.Fragment key={crumb.id}>
                  <span className="breadcrumb-sep">/</span>
                  {idx === (currentFolder.breadcrumbs?.length || 0) - 1 ? (
                    <span className="breadcrumb-current">{crumb.name}</span>
                  ) : (
                    <span
                      className="breadcrumb-link"
                      onClick={() => navigate(`/portal/resources/${crumb.slug}`)}
                    >
                      {crumb.name}
                    </span>
                  )}
                </React.Fragment>
              ))}
            </div>
            <h1>{currentFolder.name}</h1>
            {currentFolder.description && <p>{currentFolder.description}</p>}
          </>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
            <div>
              <h1>Partner Resources</h1>
              <p>Marketing materials, tutorials, and tools to help grow your business</p>
            </div>
            <TutorialHelpButton pageKey="resources" />
          </div>
        )}
      </div>

      {/* Featured Section (only on root) */}
      {!currentFolder && featured.length > 0 && (
        <section className="featured-section" data-tutorial="featured-section">
          <h2>
            <Star size={18} />
            Featured
          </h2>
          <div className="featured-scroll">
            {featured.map(resource => (
              <div
                key={resource.id}
                className="featured-card"
                onClick={() => {
                  if (resource.is_video) {
                    setSelectedVideo(resource);
                  } else {
                    handleDownload(resource);
                  }
                }}
              >
                <div className="featured-thumbnail">
                  {(() => {
                    const thumbUrl = getThumbnailUrl(resource);
                    return thumbUrl ? (
                      <>
                        <img src={thumbUrl} alt={resource.title} className="resource-image-preview" />
                        {resource.is_video && (
                          <div className="play-overlay">
                            <PlayCircle size={32} />
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="resource-icon-placeholder">
                        {React.createElement(getResourceIcon(resource.resource_type), { size: 28 })}
                      </div>
                    );
                  })()}
                </div>
                <div className="featured-info">
                  <h4>{resource.title}</h4>
                  <span className="featured-type">
                    {resource.is_video ? 'Video' : resource.file_extension || 'File'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Folders Section */}
      {(currentFolder ? subfolders : folders).length > 0 && (
        <section className="folders-section">
          <h2>{currentFolder ? 'Subfolders' : 'Folders'}</h2>
          <div className="folders-grid" data-tutorial="folders-grid">
            {(currentFolder ? subfolders : folders).map(renderFolderCard)}
          </div>
        </section>
      )}

      {/* Resources Section */}
      {currentFolder && resources.length > 0 && (
        <section className="resources-section">
          <h2>Files</h2>
          <div className="resources-grid" data-tutorial="resource-card">
            {resources.map(renderResourceCard)}
          </div>
        </section>
      )}

      {/* Empty State */}
      {currentFolder && resources.length === 0 && subfolders.length === 0 && (
        <div className="empty-state">
          <Folder size={48} />
          <h3>This folder is empty</h3>
          <p>No resources have been added to this folder yet.</p>
        </div>
      )}

      {!currentFolder && folders.length === 0 && (
        <div className="empty-state">
          <Folder size={48} />
          <h3>No resources available</h3>
          <p>Check back later for marketing materials and resources.</p>
        </div>
      )}

      {/* Video Modal */}
      {selectedVideo && (
        <div className="video-modal-overlay" onClick={() => setSelectedVideo(null)}>
          <div className="video-modal" onClick={e => e.stopPropagation()}>
            <div className="video-modal-header">
              <h3>{selectedVideo.title}</h3>
              <button className="modal-close" onClick={() => setSelectedVideo(null)}>
                <X size={24} />
              </button>
            </div>
            <div className="video-container">
              {selectedVideo.youtube_video_id ? (
                <iframe
                  src={`https://www.youtube.com/embed/${selectedVideo.youtube_video_id}?autoplay=1`}
                  title={selectedVideo.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <div className="video-error">Video not available</div>
              )}
            </div>
            {selectedVideo.description && (
              <div className="video-description">
                <p>{selectedVideo.description}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PortalResources;
